import { useRef, useState, useEffect } from "react";
import { PlayIcon, ArrowRightIcon } from "lucide-react";

import UseCasesSectionBlk from "./UseCasesSectionBlk";

import { useDemoModal } from "../hooks/useDemoModal";
import { videosAndDemos } from "../data/videos-and-demos";

export const VideosAndDemosSection = () => {
  const { onOpen } = useDemoModal();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [thumbWidth, setThumbWidth] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Calculate thumb width and track scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const updateScrollInfo = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const maxScroll = scrollWidth - clientWidth;

      // Calculate thumb width as percentage of visible area
      const visibleRatio = clientWidth / scrollWidth;
      setThumbWidth(Math.max(visibleRatio * 100, 20)); // Min 20% width

      // Calculate scroll progress
      if (maxScroll > 0) {
        setScrollProgress((scrollLeft / maxScroll) * 100);
      }
    };

    updateScrollInfo();
    container.addEventListener("scroll", updateScrollInfo);
    window.addEventListener("resize", updateScrollInfo);

    return () => {
      container.removeEventListener("scroll", updateScrollInfo);
      window.removeEventListener("resize", updateScrollInfo);
    };
  }, []);

  // Mouse drag handlers for the cards container
  const handleMouseDown = (e: React.MouseEvent) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const container = scrollContainerRef.current;
    if (!container) return;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier
    container.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Handle scrollbar click/drag
  const handleScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = scrollContainerRef.current;
    const scrollbar = e.currentTarget;
    if (!container || !scrollbar) return;

    const rect = scrollbar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const scrollbarWidth = rect.width;
    const { scrollWidth, clientWidth } = container;
    const maxScroll = scrollWidth - clientWidth;

    // Calculate where to scroll based on click position
    const clickRatio = clickX / scrollbarWidth;
    container.scrollLeft = clickRatio * maxScroll;
  };

  // Calculate thumb position
  const thumbPosition = (scrollProgress / 100) * (100 - thumbWidth);

  return (
    <section id="videos-and-demos">
      <UseCasesSectionBlk
        title="Videos and Demos"
        description="See the impact, from rapid implementations to scalable innovation, across diverse sectors and challenges "
        videoBg={false}
      />

      <div className="card-slider-section">
        <div className="main-container-blk">
          {/* Horizontal scrollable container */}
          <div
            ref={scrollContainerRef}
            className={`flex gap-4 overflow-x-auto scrollbar-hidden ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            {videosAndDemos.map((video, index) => (
              <div
                key={index}
                onClick={() => onOpen(video.title)}
                className="card-item flex-shrink-0 w-full md:w-[calc((100%-16px)/2)] lg:w-[calc((100%-32px)/3)]"
              >
                <div className="relative">
                  <img
                    src={video.thumbnail}
                    alt=""
                    className="w-full aspect-video rounded-lg object-cover"
                  />
                  <PlayIcon className="text-white fill-white size-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_4px_14px_rgba(0,0,0,0.95)]" />
                </div>

                <div className="card-content">
                  <h3>{video.title}</h3>
                  <p className="line-clamp-2">{video.description}</p>
                  <span className="text-base font-semibold text-primary-blue inline-flex items-center gap-2 transition-all duration-300 hover:gap-2.5 mt-2">
                    Watch Video <ArrowRightIcon className="size-5" />
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Custom Scrollbar */}
          {videosAndDemos.length > 3 && (
            <div
              className="w-full h-3 bg-secondary-dark-navy-500 rounded-full cursor-pointer relative mt-6"
              onClick={handleScrollbarClick}
            >
              <div
                className="h-full bg-primary-blue rounded-full absolute top-0 transition-all duration-100"
                style={{
                  width: `${thumbWidth}%`,
                  left: `${thumbPosition}%`,
                }}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
